import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { QUESTION_TYPES } from "src/utils/questionBank";
import Services from "src/services/Services";

// QuestionTypeManagerModal.jsx içinde (component üst kısmına yerleştir)
function normalizeQuestionFromApi(item) {
    const c = item?.content || {};
    const rawLabels = c.labels;
    // Bazı tiplerde labels [] dönebiliyor; objeye normalize edelim
    const labels =
        rawLabels && !Array.isArray(rawLabels) ? rawLabels : {};

    return {
        questionID: item?.questionID,
        label: c.label || "",
        type: c.type || "short",
        options: Array.isArray(c.options) ? c.options : [],
        labels,
        // İstersen meta saklamak istersen:
        _meta: {
            id: item?.id,
            createdDateTime: item?.createdDateTime,
            status: item?.status,
            createdBy: item?.createdBy,
        },
    };
}

export default function QuestionTypeManagerModal({ open, onClose }) {
    const parsedUser = JSON.parse(localStorage.getItem('user'));
    const token = parsedUser.token

    const [bank, setBank] = useState([]);
    const [editingId, setEditingId] = useState(null);

    const [label, setLabel] = useState("");
    const [type, setType] = useState("short");
    const [options, setOptions] = useState([]);
    const [optionInput, setOptionInput] = useState("");
    const [yesLabel, setYesLabel] = useState("Evet");
    const [noLabel, setNoLabel] = useState("Hayır");

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const isOptionsType = useMemo(
        () => ["multiselect", "single", "dropdown"].includes(type),
        [type]
    );
    const isYesNo = type === "yesno";

    // Modal açıldığında soruları backend’den çek
    useEffect(() => {
        if (open) {
            fetchQuestions();
            resetForm();
            setError("");
        }
    }, [open]);

    async function fetchQuestions() {
        try {
            const result = await Services.GetCampaignQuestions(token);
            console.log(result.data?.result);
            if (result.data?.status && Array.isArray(result.data?.result)) {
                const normalized = result.data.result.map(normalizeQuestionFromApi);
                setBank(normalized);
            } else {
                setBank([]);
            }
        } catch (e) {
            console.error("Get error", e);
            setBank([]);
            setError("Sorular yüklenemedi.");
        }
    }

    function resetForm() {
        setEditingId(null);
        setLabel("");
        setType("short");
        setOptions([]);
        setOptionInput("");
        setYesLabel("Evet");
        setNoLabel("Hayır");
        setError("");
    }

    function startEdit(q) {
        setEditingId(q.questionID);
        setLabel(q.label);
        setType(q.type);
        setOptions(q.options || []);
        setYesLabel(q.labels?.yesLabel || "Evet");
        setNoLabel(q.labels?.noLabel || "Hayır");
        setError("");
    }

    function handleAddOption() {
        const opt = optionInput.trim();
        if (opt && !options.includes(opt)) {
            setOptions((prev) => [...prev, opt]);
        }
        setOptionInput("");
    }

    function handleDeleteOption(opt) {
        setOptions((prev) => prev.filter((o) => o !== opt));
    }

    async function handleSaveQuestion() {
        const content = {
            label: label.trim(),
            type,
            options: isOptionsType ? options : [],
            labels: isYesNo ? { yesLabel, noLabel } : {},
        };

        if (!content.label || (isOptionsType && content.options.length === 0)) return;

        setSaving(true);
        setError("");

        try {
            if (editingId) {
                // EDIT
                const result = await Services.UpdateCampaignQuestion(token, editingId, content);
                if (result.data?.status) {
                    const saved = { ...content, questionID: editingId };
                    setBank(prev => prev.map(q => (q.questionID === editingId ? saved : q)));
                    resetForm();
                } else {
                    setError("Güncelleme başarısız.");
                }
            } else {
                // CREATE
                const result = await Services.AddCampaignQuestion(token, content);
                if (result.data?.status && result.data?.result?.questionID) {
                    const saved = { ...content, questionID: result.data.result.questionID };
                    setBank((prev) => [...prev, saved]);
                    resetForm();
                } else {
                    setError("Kayıt başarısız.");
                }
            }
        } catch (e) {
            console.error(e);
            setError("Beklenmeyen bir hata oluştu.");
        } finally {
            setSaving(false);
        }
    }

    async function handleDeleteQuestion(id) {
        try {
            const result = await Services.RemoveCampaignQuestion(token, id);
            console.log(result.data);
            if (result.data?.status) {
                setBank((prev) => prev.filter((q) => q.questionID !== id));
                if (editingId === id) resetForm();
            } else {
                setError("Silme başarısız.");
            }
        } catch (e) {
            console.error("Delete error", e);
            setError("Beklenmeyen bir hata oluştu.");
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
        >
            <Box
                sx={{
                    bgcolor: "#fff",
                    p: 3,
                    borderRadius: 2,
                    width: 900,
                    maxHeight: "85vh",
                    overflowY: "auto",
                    boxShadow: "0 12px 28px rgba(0,0,0,.18)",
                }}
            >
                <Typography variant="h6" gutterBottom>
                    Soru Tipi Ekle / Düzenle
                </Typography>

                {/* Form */}
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                    <TextField
                        label="Soru Başlığı"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        fullWidth
                    />
                    <FormControl fullWidth>
                        <InputLabel>Soru Tipi</InputLabel>
                        <Select
                            value={type}
                            label="Soru Tipi"
                            onChange={(e) => setType(e.target.value)}
                        >
                            {QUESTION_TYPES.map((t) => (
                                <MenuItem key={t.value} value={t.value}>
                                    {t.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {isOptionsType && (
                    <Box mb={2}>
                        <Box display="flex" gap={1} alignItems="center">
                            <TextField
                                label="Seçenek ekle"
                                value={optionInput}
                                onChange={(e) => setOptionInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && handleAddOption()}
                                size="small"
                                sx={{ width: 320 }}
                            />
                            <Button onClick={handleAddOption}>Ekle</Button>
                        </Box>
                        <Box mt={1}>
                            {options.map((opt) => (
                                <Chip
                                    key={opt}
                                    label={opt}
                                    onDelete={() => handleDeleteOption(opt)}
                                    sx={{ mr: 0.5, mb: 0.5 }}
                                />
                            ))}
                        </Box>
                    </Box>
                )}

                {isYesNo && (
                    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={2}>
                        <TextField
                            label="Evet Label"
                            value={yesLabel}
                            onChange={(e) => setYesLabel(e.target.value)}
                        />
                        <TextField
                            label="Hayır Label"
                            value={noLabel}
                            onChange={(e) => setNoLabel(e.target.value)}
                        />
                    </Box>
                )}

                <Box display="flex" gap={1} justifyContent="flex-end" mb={2}>
                    <Button variant="outlined" onClick={resetForm} disabled={saving}>
                        Temizle
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSaveQuestion}
                        disabled={
                            saving || !label.trim() || (isOptionsType && options.length === 0)
                        }
                    >
                        {saving
                            ? "Kaydediliyor..."
                            : editingId
                                ? "Soru Güncelle"
                                : "Soru Ekle"}
                    </Button>
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography sx={{ fontWeight: 600, mb: 1 }}>Sorular</Typography>
                <Box>
                    {bank.map((q) => (
                        <Box
                            key={q.questionID}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ p: 1.2, border: "1px solid #eee", borderRadius: 1, mb: 1 }}
                        >
                            <Box>
                                <Typography sx={{ fontWeight: 600 }}>{q.label}</Typography>
                                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                                    {q.type}
                                </Typography>
                                {q.options?.length ? (
                                    <Box
                                        mt={0.5}
                                        sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                                    >
                                        {q.options.map((o) => (
                                            <Chip key={o} size="small" label={o} />
                                        ))}
                                    </Box>
                                ) : null}
                            </Box>
                            <Box>
                                <IconButton size="small" onClick={() => startEdit(q)}>
                                    <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => handleDeleteQuestion(q.questionID)}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>
                    ))}
                </Box>

                <Box mt={2} display="flex" justifyContent="flex-end">
                    <Button onClick={onClose}>Kapat</Button>
                </Box>
            </Box>
        </Modal>
    );
}